import React, { useState } from 'react';
import { Card, Button, Typography, Steps, message, Alert, Spin, Table, Input } from 'antd';
import { PlayCircleOutlined, SendOutlined, ReloadOutlined, CheckCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { Step } = Steps;
const { TextArea } = Input;

const FileProcessingWidget = ({
    uploadedFile,
    selectedTemplate,
    columnMappings,
    onProcessingComplete,
    onReset,
    apiBaseUrl,
    authToken
}) => {
    const [loading, setLoading] = useState(false);
    const [processedFile, setProcessedFile] = useState(null);
    const [finalFilename, setFinalFilename] = useState('');
    const [generatedPreview, setGeneratedPreview] = useState(null);
    const [fileType, setFileType] = useState('');
    const [step, setStep] = useState(0);
    const [fileDescription, setFileDescription] = useState('');

    const steps = [
        {
            title: 'Generate File',
            description: 'Process file according to template and mappings',
        },
        {
            title: 'Send to Invent',
            description: 'Send file to Invent (catalog or storage)',
        },
        {
            title: 'Complete',
            description: 'File successfully processed and sent',
        },
    ];

    const handleGenerateFile = async () => {
        setLoading(true);

        try {
            const formData = new FormData();
            formData.append('file', uploadedFile);
            const templateKey = selectedTemplate.templateKey || selectedTemplate.name.toLowerCase().replace(/\s+/g, '_');
            formData.append('template_name', templateKey);
            formData.append('mappings', JSON.stringify(columnMappings));

            const response = await fetch(`${apiBaseUrl}/process`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                },
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setProcessedFile(data.file_id);
                setFinalFilename(data.final_filename);
                setGeneratedPreview(data.preview_data);
                setFileType(data.file_type);
                setStep(1); // Move to Send step

                message.success('File generated successfully!');
                onProcessingComplete(data.file_id);
            } else {
                message.error('Failed to generate file');
            }
        } catch (error) {
            message.error(`File generation failed: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleSendToInvent = async () => {
        setLoading(true);

        try {
            const response = await fetch(`${apiBaseUrl}/send-to-azure`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    file_id: processedFile,
                    filename: finalFilename,
                    template_name: selectedTemplate?.filename_template || 'unknown',
                    description: fileDescription?.trim() || undefined,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setStep(2); // Move to Complete step
                const msg = data.message || 'File sent to Invent successfully!';
                message.success(data.staging_used ? `Azure → Iceberg. ${msg}` : msg);
            } else {
                message.error('Failed to send file to Invent');
            }
        } catch (error) {
            message.error(`Send failed: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleReset = () => {
        setProcessedFile(null);
        setFinalFilename('');
        setGeneratedPreview(null);
        setFileType('');
        setStep(0);
        setFileDescription('');
    };

    const handleStartOver = () => {
        onReset();
    };

    const getStepStatus = (stepIndex) => {
        if (stepIndex < step) return 'finish';
        if (stepIndex === step) return 'process';
        return 'wait';
    };

    return (
        <div>
            <Title level={4}>Process and Send File</Title>
            <Text type="secondary">
                Generate the final file according to your mappings and send it to Invent (catalog or storage).
            </Text>

            <div style={{ marginTop: 24 }}>
                <Steps current={step} status={getStepStatus(step)}>
                    {steps.map((stepItem, index) => (
                        <Step
                            key={index}
                            title={stepItem.title}
                            description={stepItem.description}
                            icon={
                                index === 2 && step === 2 ? (
                                    <CheckCircleOutlined />
                                ) : undefined
                            }
                        />
                    ))}
                </Steps>
            </div>

            <div style={{ marginTop: 32 }}>
                {step === 0 && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <Title level={4}>Ready to Generate File</Title>
                            <div style={{ marginBottom: 24 }}>
                                <Text>
                                    <strong>Template:</strong> {selectedTemplate?.name}
                                </Text>
                                <br />
                                <Text>
                                    <strong>Mapped Fields:</strong> {columnMappings.length}
                                </Text>
                                <br />
                                <Text>
                                    <strong>Output Filename:</strong> {selectedTemplate?.filename_template}_YYYY-MM-DDTHH-MM-SS.csv
                                </Text>
                            </div>

                            <Button
                                type="primary"
                                size="large"
                                icon={<PlayCircleOutlined />}
                                onClick={handleGenerateFile}
                                loading={loading}
                            >
                                Generate File
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 1 && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <Title level={4}>File Generated Successfully</Title>
                            <div style={{ marginBottom: 24 }}>
                                <Text>
                                    <strong>Final Filename:</strong> {finalFilename}
                                </Text>
                                <br />
                                <Text>
                                    <strong>File Type:</strong> {fileType}
                                </Text>
                            </div>

                            {/* PREVIEW TABLE */}
                            {generatedPreview && Array.isArray(generatedPreview) && generatedPreview.length > 0 && generatedPreview[0] && (
                                <div style={{ marginBottom: 24, textAlign: 'left' }}>
                                    <div style={{ marginBottom: 16 }}>
                                        <Text strong>Columns: </Text>
                                        <Text>{Object.keys(generatedPreview[0] || {}).length}</Text>
                                        <br />
                                        <Text strong>Preview rows: </Text>
                                        <Text>{generatedPreview.length}</Text>
                                    </div>

                                    <Table
                                        dataSource={generatedPreview.map((row, index) => ({ ...row, key: index }))}
                                        columns={Object.keys(generatedPreview[0] || {}).map(key => ({
                                            title: key,
                                            dataIndex: key,
                                            key: key,
                                            ellipsis: true,
                                            render: (text) => text !== null && text !== undefined ? String(text) : '',
                                        }))}
                                        pagination={false}
                                        size="small"
                                        scroll={{ x: true }}
                                    />
                                </div>
                            )}

                            {(!generatedPreview || !Array.isArray(generatedPreview) || generatedPreview.length === 0 || !generatedPreview[0]) && (
                                <Alert
                                    message="No Preview Available"
                                    description="The generated file has no preview data, but the file was created successfully."
                                    type="warning"
                                    style={{ marginBottom: 24 }}
                                    showIcon
                                />
                            )}

                            <div style={{ marginBottom: 24, textAlign: 'left' }}>
                                <Text strong>Description (optional)</Text>
                                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                                    Add a description of what this file is about. Stored as file_description in the table.
                                </Text>
                                <TextArea
                                    placeholder="e.g. Monthly reconciliation for January 2025"
                                    value={fileDescription}
                                    onChange={(e) => setFileDescription(e.target.value)}
                                    rows={2}
                                    maxLength={2000}
                                    showCount
                                />
                            </div>

                            <Button
                                type="primary"
                                size="large"
                                icon={<SendOutlined />}
                                onClick={handleSendToInvent}
                                loading={loading}
                            >
                                Send to Invent
                            </Button>
                        </div>
                    </Card>
                )}

                {step === 2 && (
                    <Card>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                            <CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a', marginBottom: 16 }} />
                            <Title level={4} style={{ color: '#52c41a' }}>
                                Process Completed Successfully!
                            </Title>
                            <div style={{ marginBottom: 24 }}>
                                <Text>
                                    Your file <Text code>{finalFilename}</Text> has been successfully processed and sent to Invent.
                                </Text>
                            </div>

                            <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
                                <Button
                                    size="large"
                                    icon={<ReloadOutlined />}
                                    onClick={handleReset}
                                >
                                    Process Another File
                                </Button>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ReloadOutlined />}
                                    onClick={handleStartOver}
                                >
                                    Start Over
                                </Button>
                            </div>
                        </div>
                    </Card>
                )}

                {loading && (
                    <div style={{ textAlign: 'center', marginTop: 24 }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            <Text>
                                {step === 0 && 'Generating file...'}
                                {step === 1 && 'Sending to Invent...'}
                            </Text>
                        </div>
                    </div>
                )}
            </div>

            <Alert
                message="Important"
                description="The processed file will be automatically deleted from the server after successful send to maintain security and storage efficiency."
                type="info"
                showIcon
                style={{ marginTop: 24 }}
            />
        </div>
    );
};

export default FileProcessingWidget;
